import { handleActions } from 'redux-actions';
import { DamzState, AttachmentType } from '../../types';
import { setEditMessage, cancelEditMessage } from '../actions/room';
import {
  addDraftAttachment,
  addDraftUser,
  allowDraftMessage,
  clearDraft,
  clearDrafts,
  disallowDraftMessage,
  hydrateDraft,
  logUnfurlLink,
  replaceUnfurlLog,
  removeDraftAttachment,
  removeDraftUser,
  removeDraftUserAll,
  setDraftMessage,
  setDraftParticipants,
  addUploadProgressAttachment,
  updateUploadProgress,
  completeImageUpload,
  failImageUpload,
  cancelImageUpload,
  logDeviationsNotFound,
} from '../actions/drafts';
import isEmpty from 'lodash/isEmpty';
import ddt from '@wix/da-ddt';
import { getAttachmentsFromMessageItem } from '../../utils/attachments';
import { getCanEditMessage } from '../../utils/getCanEditMessage';
import { DEFAULT_DRAFT_STATE } from '../helpers';
import omit from 'lodash/omit';

export default handleActions<DamzState['drafts'], any>(
  {
    [`${setEditMessage}`]: (
      state,
      action: ReturnType<typeof setEditMessage>
    ) => ({
      ...state,
      messages: {
        ...state.messages,
        [action.payload.roomId]: {
          ts: Date.now(),
          text: action.payload.message.msg.msg,
          attachments: getAttachmentsFromMessageItem(action.payload.message),
          editMessageId: action.payload.message.msg.message_id,
        },
      },
    }),
    [`${cancelEditMessage}`]: (
      state,
      action: ReturnType<typeof cancelEditMessage>
    ) => ({
      ...state,
      messages: {
        ...omit(state.messages, action.payload.roomId),
      },
    }),
    [`${hydrateDraft}`]: (state, action: ReturnType<typeof hydrateDraft>) => {
      // Only if there is no data already
      if (
        !action.payload.data ||
        !isEmpty(state.participants) ||
        !isEmpty(state.messages)
      ) {
        ddt.log('damz', 'Hydrate draft skipped', action);
        return state;
      }

      // Since we can have drafts for messages that are no longer editable, we need to prune them if their edit window has expired
      // Also trim empty message drafts while we're here.
      const prunedMessages = Object.entries(
        action.payload.data.messages
      ).reduce((acc, [room_id, message]) => {
        if (!message.editMessageId && message.text?.trim() !== '') {
          return {
            ...acc,
            [room_id]: message,
          };
        } else if (
          !!message.editMessageId &&
          getCanEditMessage(`${message.ts}`, true)
        ) {
          return {
            ...acc,
            [room_id]: message,
          };
        }
        return acc;
      }, {});

      return {
        ...state,
        messages: {
          ...state.messages,
          ...(prunedMessages ?? {}),
        },
        participants: action.payload.data.participants ?? [],
      };
    },
    [`${setDraftMessage}`]: (
      state,
      action: ReturnType<typeof setDraftMessage>
    ) => {
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.room_id]: {
            text: action.payload.text,
            ts: Date.now(),
            attachments:
              state.messages[action.payload.room_id]?.attachments ?? [],
            editMessageId: action.payload.editMessageId,
          },
        },
      };
    },
    [`${clearDraft}`]: (state, action: ReturnType<typeof clearDraft>) => {
      return {
        ...state,
        messages: {
          ...omit(state.messages, action.payload.room_id),
        },
        participants: [],
        unfurlLog: {},
        unfurlResolving: false,
      };
    },
    [`${clearDrafts}`]: (state, action: ReturnType<typeof clearDrafts>) => {
      return {
        ...state,
        participants: [],
        messages: {},
      };
    },
    [`${setDraftParticipants}`]: (
      state,
      action: ReturnType<typeof setDraftParticipants>
    ) => {
      return {
        ...state,
        participants: action.payload.participants,
      };
    },
    [`${addDraftAttachment}`]: (
      state,
      action: ReturnType<typeof addDraftAttachment>
    ) => {
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.room_id]: {
            ...(state.messages[action.payload.room_id] ?? {}),
            attachments: [
              ...(state.messages[action.payload.room_id]?.attachments ?? []),
              ...[action.payload.attachment],
            ],
          },
        },
      };
    },
    [`${removeDraftAttachment}`]: (
      state,
      action: ReturnType<typeof removeDraftAttachment>
    ) => {
      const attachments =
        state.messages[action.payload.room_id]?.attachments ?? [];

      if (attachments.length === 0) {
        return state;
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.room_id]: {
            ...(state.messages[action.payload.room_id] ?? {}),
            attachments: [
              ...attachments.slice(0, action.payload.index),
              ...attachments.slice(action.payload.index + 1),
            ],
          },
        },
      };
    },
    [`${logDeviationsNotFound}`]: (
      state,
      action: ReturnType<typeof logDeviationsNotFound>
    ) => {
      return {
        ...state,
        deviationsNotFound: [
          ...state.deviationsNotFound,
          ...action.payload.deviationIds,
        ],
      };
    },
    [`${logUnfurlLink}`]: (state, action: ReturnType<typeof logUnfurlLink>) => {
      return {
        ...state,
        unfurlLog: {
          [action.payload.room_id]: [
            ...(state.unfurlLog[action.payload.room_id] ?? []),
            ...action.payload.links,
          ],
        },
      };
    },
    [`${replaceUnfurlLog}`]: (
      state,
      action: ReturnType<typeof replaceUnfurlLog>
    ) => {
      return {
        ...state,
        unfurlLog: {
          [action.payload.room_id]: action.payload.links,
        },
      };
    },
    [`${addDraftUser}`]: (state, action: ReturnType<typeof addDraftUser>) => {
      return {
        ...state,
        participants: state.participants.find(
          user => user.userId === action.payload.user.userId
        )
          ? state.participants
          : [...state.participants, action.payload.user],
      };
    },
    [`${removeDraftUserAll}`]: (
      state,
      action: ReturnType<typeof removeDraftUserAll>
    ) => {
      return {
        ...state,
        participants: [],
      };
    },
    [`${removeDraftUser}`]: (
      state,
      action: ReturnType<typeof removeDraftUser>
    ) => {
      return {
        ...state,
        participants: [
          ...state.participants.filter(
            user => user.userId !== action.payload.user.userId
          ),
        ],
      };
    },
    [`${disallowDraftMessage}`]: (
      state,
      action: ReturnType<typeof disallowDraftMessage>
    ) => {
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.room_id]: {
            ...(state.messages[action.payload.room_id] ?? {}),
            isMessageAllowed: false,
          },
        },
      };
    },
    [`${allowDraftMessage}`]: (
      state,
      action: ReturnType<typeof allowDraftMessage>
    ) => {
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.room_id]: {
            ...(state.messages[action.payload.room_id] ?? {}),
            isMessageAllowed: true,
          },
        },
      };
    },
    [`${addUploadProgressAttachment}`]: (
      state,
      action: ReturnType<typeof addUploadProgressAttachment>
    ) => {
      const { roomId, fileId, file } = action.payload;

      return {
        ...state,
        messages: {
          ...state.messages,
          [roomId]: {
            ...(state.messages[roomId] ?? {}),
            attachments: [
              ...(state.messages[roomId]?.attachments ?? []),
              {
                attachmentType: AttachmentType.UploadProgress,
                fileId,
                file,
                progress: 0,
                status: 'uploading',
                link: '', // Required by BaseAttachment
              },
            ],
          },
        },
      };
    },
    [`${updateUploadProgress}`]: (
      state,
      action: ReturnType<typeof updateUploadProgress>
    ) => {
      const { roomId, fileId, progress } = action.payload;

      return {
        ...state,
        messages: {
          ...state.messages,
          [roomId]: {
            ...state.messages[roomId],
            attachments:
              state.messages[roomId]?.attachments?.map(att =>
                att.attachmentType === AttachmentType.UploadProgress &&
                (att as any).fileId === fileId
                  ? { ...att, progress }
                  : att
              ) ?? [],
          },
        },
      };
    },
    [`${completeImageUpload}`]: (
      state,
      action: ReturnType<typeof completeImageUpload>
    ) => {
      const { roomId, fileId, stashItem } = action.payload;

      return {
        ...state,
        messages: {
          ...state.messages,
          [roomId]: {
            ...state.messages[roomId],
            attachments:
              state.messages[roomId]?.attachments
                ?.filter(
                  att =>
                    !(
                      att.attachmentType === AttachmentType.UploadProgress &&
                      (att as any).fileId === fileId
                    )
                )
                ?.concat(stashItem) ?? [],
          },
        },
      };
    },
    [`${failImageUpload}`]: (
      state,
      action: ReturnType<typeof failImageUpload>
    ) => {
      const { roomId, fileId, error } = action.payload;

      return {
        ...state,
        messages: {
          ...state.messages,
          [roomId]: {
            ...state.messages[roomId],
            attachments:
              state.messages[roomId]?.attachments?.map(att =>
                att.attachmentType === AttachmentType.UploadProgress &&
                (att as any).fileId === fileId
                  ? { ...att, status: 'error', error }
                  : att
              ) ?? [],
          },
        },
      };
    },
    [`${cancelImageUpload}`]: (
      state,
      action: ReturnType<typeof cancelImageUpload>
    ) => {
      const { roomId, fileId } = action.payload;

      return {
        ...state,
        messages: {
          ...state.messages,
          [roomId]: {
            ...state.messages[roomId],
            attachments:
              state.messages[roomId]?.attachments?.filter(
                att =>
                  !(
                    att.attachmentType === AttachmentType.UploadProgress &&
                    (att as any).fileId === fileId
                  )
              ) ?? [],
          },
        },
      };
    },
  },
  DEFAULT_DRAFT_STATE
);
